import React, { FC, useState } from "react";
import {
  Grid,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Box,
  Zoom,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import isEmpty from "validator/lib/isEmpty";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  fetchPlayerFromLicence,
  fetchedErrorMessage,
  fetchedNoPlayer,
  fetchedSearchError,
  getPlayerLoading,
} from "../../features/player/playerSlice";
import {
  fetchBoards,
  fetchTournoi,
  getAreOpenedRegistrations,
  getAreOpenedPreRegistrations,
  getBoards,
  getOrganizerClubIds,
  getRegistrationPhase,
  getTournoi,
} from "../../features/tournoi/tournoiSlice";
import BackgroundWrapper from "../BackgroundWrapper";
import CustomTextField from "../Base/CustomTextField";
import { HOME_REGISTRATION_OPEN_TIME_LABEL } from "../Home/homeConstants";
import type { RegistrationPhase, Tableau } from "../../types";
import { setRegisterEntryMode } from "../../utils/registerFlow";
import {
  isBlockedByWomenOnlyTournoi,
  WOMEN_ONLY_BLOCK_MESSAGE,
} from "../../utils/tournoiEligibility";

/** Libellé court pour l’alerte (évite le label admin trop verbeux). */
function formatOnSiteBoardLabel(board: Tableau): string {
  const name = board.name?.trim() || "?";
  const label = board.label?.trim();
  if (!label) {
    return `tableau ${name}`;
  }
  const shortLabel = label.split(/\s*[—(]/)[0]?.trim() || label;
  return `tableau ${name} (${shortLabel})`;
}

function onSiteRegistrationInfoMessage(boards: Tableau[]): string {
  const labels = boards.map(formatOnSiteBoardLabel);
  const inscriptionTarget =
    labels.length === 1
      ? `au ${labels[0]}`
      : `aux ${labels.join(", ")}`;
  return `Ce parcours en ligne est réservé aux licenciées FFTT. Sans licence, l’inscription ${inscriptionTarget} se fait sur place le jour du tournoi.`;
}

/** Message affiché sur /register quand l’utilisateur ne peut pas accéder au parcours d’inscription. */
function getRegisterBlockedMessage(phase: RegistrationPhase): string {
  switch (phase) {
    case "closed":
      return "Les inscriptions en ligne ne sont pas encore ouvertes.";
    case "preRegistration":
      return "Les inscriptions en ligne ne sont pas encore ouvertes.";
    case "closedForPlayers":
      return "Les inscriptions en ligne sont fermées. Le tournoi va bientôt commencer.";
    case "cancelled":
      return "Le tournoi a été annulé. Les inscriptions en ligne sont définitivement closes.";
    default:
      return "Les inscriptions ne sont pas disponibles pour le moment.";
  }
}

type RegisterMode = "standard" | "preRegistration";

function getPreRegistrationBlockedMessage(phase: RegistrationPhase): string {
  switch (phase) {
    case "closed":
      return "La pré-inscription n'est pas encore ouverte.";
    case "open":
      return "La pré-inscription est terminée. Les inscriptions sont désormais ouvertes à tous.";
    case "closedForPlayers":
      return "La pré-inscription est terminée.";
    case "cancelled":
      return "Le tournoi a été annulé.";
    default:
      return "La pré-inscription n'est pas disponible pour le moment.";
  }
}

interface RegisterProps {
  mode?: RegisterMode;
}

const Register: FC<RegisterProps> = ({ mode = "standard" }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [licenceId, setLicenceId] = useState<string>("");
  const [isLicenceValid, setIsLicenceValid] = useState<boolean>(false);
  const [isLicenceDirty, setIsLicenceDirty] = useState<boolean>(false);
  const [ineligibleError, setIneligibleError] = useState<boolean>(false);
  const [womenOnlyError, setWomenOnlyError] = useState<boolean>(
    Boolean(
      (location.state as { womenOnlyBlocked?: boolean } | null)?.womenOnlyBlocked
    )
  );

  React.useEffect(() => {
    if (
      (location.state as { womenOnlyBlocked?: boolean } | null)?.womenOnlyBlocked
    ) {
      setWomenOnlyError(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const noPlayer = useAppSelector(fetchedNoPlayer);
  const errorMessage: string = useAppSelector(fetchedErrorMessage);
  const searchError = useAppSelector(fetchedSearchError);
  const areOpenedRegistrations = useAppSelector(getAreOpenedRegistrations);
  const areOpenedPreRegistrations = useAppSelector(getAreOpenedPreRegistrations);
  const organizerClubIds = useAppSelector(getOrganizerClubIds);
  const registrationPhase = useAppSelector(getRegistrationPhase);
  const tournoi = useAppSelector(getTournoi);
  const boards = useAppSelector(getBoards);
  const loading = useAppSelector(getPlayerLoading);
  const isPreRegistrationMode = mode === "preRegistration";
  const canAccessRegistrationPage = isPreRegistrationMode
    ? areOpenedPreRegistrations
    : areOpenedRegistrations;
  const blockedMessage = isPreRegistrationMode
    ? getPreRegistrationBlockedMessage(registrationPhase)
    : getRegisterBlockedMessage(registrationPhase);
  const onSiteBoards = React.useMemo(
    () => boards.filter((b) => b.inscriptionSurPlace === true),
    [boards]
  );

  React.useEffect(() => {
    setRegisterEntryMode(isPreRegistrationMode ? "preRegistration" : "standard");
  }, [isPreRegistrationMode]);

  React.useEffect(() => {
    if (!canAccessRegistrationPage || !tournoi?.id) return;
    // Toujours recharger : un cache Redux sans `inscriptionSurPlace` masquerait l’info.
    void dispatch(fetchBoards());
  }, [canAccessRegistrationPage, tournoi?.id, dispatch]);

  const handleLicenceChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const licenceIdValue: string = e.target.value;
    setLicenceId(licenceIdValue);
    setIsLicenceValid(!isEmpty(licenceIdValue));
    setIneligibleError(false);
  };

  if (!canAccessRegistrationPage) {
    return (
      <Zoom in>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
        <Box
          sx={(theme) => ({
            maxWidth: 600,
            width: "100%",
            ...(theme.palette.mode === "dark"
              ? {
                  bgcolor: alpha(theme.palette.secondary.main, 0.12),
                  color: theme.palette.grey[50],
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                  borderLeft: `4px solid ${theme.palette.secondary.main}`,
                }
              : {
                  bgcolor: theme.palette.secondary.light,
                  color: theme.palette.text.primary,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                  borderLeft: `4px solid ${theme.palette.secondary.main}`,
                }),
            borderRadius: 2,
            p: 3,
            boxShadow: 2,
            textAlign: "center",
            zIndex: 1,
          })}
        >
          <Typography variant="body1" color="inherit">
            {blockedMessage}
          </Typography>
        </Box>
        </Box>
      </Zoom>
    );
  }

  return (
    <BackgroundWrapper>
      <Zoom in>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              maxWidth: 600,
            }}
          >
          <Grid
            container
            direction="column"
            rowSpacing={4}
            sx={{
              width: "100%",
              p: 2,
            }}
          >
            {isPreRegistrationMode && registrationPhase === "preRegistration" && (
              <Grid item>
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={(theme) => ({
                    fontWeight: 500,
                    borderLeft: `4px solid ${theme.palette.warning.main}`,
                    ...(theme.palette.mode === "dark" && {
                      color: theme.palette.grey[100],
                      borderColor: alpha(theme.palette.warning.main, 0.55),
                      bgcolor: alpha(theme.palette.warning.main, 0.08),
                      "& .MuiAlert-icon": { color: theme.palette.warning.light },
                    }),
                  })}
                >
                  Vous êtes sur la page de <strong>pré-inscription</strong>, réservée aux joueurs de SQY PING et aux administrateurs.
                  {tournoi?.dateOuvertureInscriptions
                    ? ` Ouverture publique le ${tournoi.dateOuvertureInscriptions} à ${HOME_REGISTRATION_OPEN_TIME_LABEL}.`
                    : ""}
                </Alert>
              </Grid>
            )}

            {onSiteBoards.length > 0 && (
              <Grid item>
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={(theme) => ({
                    borderLeft: `4px solid ${theme.palette.info.main}`,
                    ...(theme.palette.mode === "dark" && {
                      color: theme.palette.grey[100],
                      borderColor: alpha(theme.palette.info.main, 0.55),
                      bgcolor: alpha(theme.palette.info.main, 0.08),
                      "& .MuiAlert-icon": { color: theme.palette.info.light },
                    }),
                  })}
                >
                  {onSiteRegistrationInfoMessage(onSiteBoards)}
                </Alert>
              </Grid>
            )}

            <Grid item>
              <Box
                sx={(theme) =>
                  theme.palette.mode === "dark"
                    ? {
                        bgcolor: alpha(theme.palette.secondary.main, 0.12),
                        color: theme.palette.grey[50],
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                        borderLeft: `4px solid ${theme.palette.secondary.main}`,
                        boxShadow: "none",
                      }
                    : {
                        bgcolor: theme.palette.secondary.light,
                        color: theme.palette.text.primary,
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: alpha(theme.palette.secondary.main, 0.35),
                        borderLeft: `4px solid ${theme.palette.secondary.main}`,
                        boxShadow: 1,
                      }
                }
              >
                <Typography variant="body1">
                  Afin de pouvoir vous inscrire au tournoi, merci de saisir
                  votre numéro de licence.
                </Typography>
              </Box>
            </Grid>

            <Grid item>
              <CustomTextField
                type="text"
                label="Numéro de Licence"
                autoFocus
                required
                fullWidth
                value={licenceId}
                onChange={handleLicenceChange}
                onBlur={() => setIsLicenceDirty(true)}
                helperText={
                  isLicenceDirty && !isLicenceValid
                    ? "Le numéro de licence est requis."
                    : undefined
                }
                error={(isLicenceDirty && !isLicenceValid) || noPlayer}
              />
            </Grid>

            {(errorMessage || searchError) && (
              <Grid item>
                <Alert
                  severity="error"
                  variant="standard"
                  icon={false}
                  sx={(theme) => ({
                    borderLeft: `4px solid ${theme.palette.error.main}`,
                  })}
                >
                  {errorMessage ||
                    "Erreur lors de la recherche de votre licence. Réessayez plus tard."}
                </Alert>
              </Grid>
            )}

            {ineligibleError && (
              <Grid item>
                <Alert
                  severity="error"
                  variant="outlined"
                  sx={(theme) => ({
                    borderLeft: `4px solid ${theme.palette.error.main}`,
                  })}
                >
                  Votre club n'est pas autorisé à s'inscrire pendant la période de pré-inscription.
                  {tournoi?.dateOuvertureInscriptions
                    ? ` Les inscriptions seront ouvertes à tous le ${tournoi.dateOuvertureInscriptions} à ${HOME_REGISTRATION_OPEN_TIME_LABEL}.`
                    : " Revenez lors de l'ouverture publique."}
                </Alert>
              </Grid>
            )}

            {womenOnlyError && (
              <Grid item>
                <Alert
                  severity="error"
                  variant="outlined"
                  sx={(theme) => ({
                    borderLeft: `4px solid ${theme.palette.error.main}`,
                  })}
                >
                  {WOMEN_ONLY_BLOCK_MESSAGE}
                </Alert>
              </Grid>
            )}

            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                disabled={!isLicenceValid || loading}
                fullWidth
                sx={{ py: 1.25, fontWeight: 700 }}
                onClick={async () => {
                  setIneligibleError(false);
                  setWomenOnlyError(false);
                  const [fetchedPlayer, latestTournoi] = await Promise.all([
                    dispatch(fetchPlayerFromLicence(licenceId)).unwrap(),
                    dispatch(fetchTournoi()).unwrap(),
                  ]);
                  if (fetchedPlayer) {
                    if (
                      isBlockedByWomenOnlyTournoi(
                        latestTournoi ?? tournoi,
                        fetchedPlayer
                      )
                    ) {
                      setWomenOnlyError(true);
                      return;
                    }
                    // Vérifier l'éligibilité club après fetch du joueur
                    // On compare directement clubId du joueur fraîchement chargé
                    if (
                      registrationPhase === "preRegistration" &&
                      organizerClubIds.length > 0 &&
                      !organizerClubIds.includes(fetchedPlayer.clubId)
                    ) {
                      setIneligibleError(true);
                      return;
                    }
                    navigate("/register/licence-validation");
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Confirmer les informations
                  {loading && (
                    <CircularProgress
                      color="inherit"
                      size={20}
                      sx={{ ml: 2 }}
                    />
                  )}
                </Box>
              </Button>
            </Grid>
          </Grid>
          </Box>
        </Box>
      </Zoom>
    </BackgroundWrapper>
  );
};

export default Register;
